import * as Sentry from '@sentry/browser';
import { LitElement, html } from 'lit';
import '../quote/quote-header.js';
import '../quote/quote-class-section.js';
import '../quote/quote-financial-summary.js';
import '../quote/quote-acceptance-section.js';
import '../quote/print-upsell-card.js';
import '../icons/warning-icon.js';
import { buildTermsWithSpecialTerms } from '../../utils/special-terms.js';

class QuotePendingAcceptance extends LitElement {
    static properties = {
        quoteReference: { type: String },
        quoteData: { type: Object },
    };

    createRenderRoot() {
        return this; // Light DOM for Tailwind compatibility
    }

    constructor() {
        super();
        this.quoteData = null;
    }

    get serviceClasses() {
        return this.quoteData?.class || [];
    }

    get rmContactName() {
        return this.quoteData?.rmContact || 'REACH Team';
    }

    get rmContactEmail() {
        return this.quoteData?.rmContactEmail || 'info@reach.nz';
    }

    connectedCallback() {
        super.connectedCallback();
        if (this.quoteData && this.serviceClasses.length === 0) {
            Sentry.captureMessage('Quote has no service classes in acceptance flow', {
                level: 'error',
                tags: { feature: 'quote-acceptance', quote_reference: this.quoteReference },
                extra: {
                    quoteStatus: this.quoteData?.quoteStatus,
                    isMUF: this.quoteData?.isMUF,
                },
            });
        }
    }

    render() {
        if (!this.quoteData) {
            return html`<div></div>`;
        }

        const hasClasses = this.serviceClasses.length > 0;

        return html`
            <div class="space-y-6 py-6">
                <div class="mb-8">
                    <h2 class="text-3xl font-bold mb-2">
                        Here's Your Quote Summary to Review & Accept
                    </h2>
                    <p class="text-gray-600">
                        Quote Reference: <span class="font-bold">${this.quoteReference}</span>
                    </p>
                </div>

                <!-- Quote Header Component -->
                <quote-header .quoteData="${this.quoteData}"></quote-header>

                ${hasClasses
                    ? html`
                          <!-- Service Classes -->
                          <div class="space-y-6 mt-8">
                              ${this.serviceClasses.map(
                                  (classData) => html`
                                      <quote-class-section
                                          .classData="${classData}"
                                          .sputnikQuoteId="${this.quoteData?.sputnikQuoteId}"
                                      ></quote-class-section>
                                  `
                              )}

                              <!-- Print Upsell (gated on VITE_FEATURE_PRINT_UPSELL env var) -->
                              ${import.meta.env.VITE_FEATURE_PRINT_UPSELL === 'true'
                                  ? html`
                                        <print-upsell-card
                                            .quoteData="${this.quoteData}"
                                            .quoteReference="${this.quoteReference}"
                                            @reload-quote="${this._handleReloadQuote}"
                                        ></print-upsell-card>
                                    `
                                  : ''}
                          </div>

                          <!-- Financial Summary -->
                          <quote-financial-summary
                              .quoteData="${this.quoteData}"
                          ></quote-financial-summary>

                          <!-- Quote Acceptance Section -->
                          <quote-acceptance-section
                              .terms="${buildTermsWithSpecialTerms(this.quoteData)}"
                              @quote-accepted="${this._handleQuoteAccepted}"
                          ></quote-acceptance-section>
                      `
                    : html`
                          <div class="alert alert-row alert-warning mt-8">
                              <warning-icon class="shrink-0"></warning-icon>
                              <span class="text-sm text-left">
                                  This quote cannot be processed because it does not contain
                                  required service details. Please contact your account manager
                                  ${this.rmContactName} for assistance.
                              </span>
                          </div>

                          <div class="flex gap-3 justify-center flex-wrap mt-6">
                              <a
                                  href="mailto:${this
                                      .rmContactEmail}?subject=Quote%20${encodeURIComponent(
                                      this.quoteReference
                                  )}%20-%20Missing%20Services"
                                  class="btn btn-primary"
                              >
                                  Email Account Manager
                              </a>
                              <a
                                  href="mailto:info@reach.nz?subject=Quote%20${encodeURIComponent(
                                      this.quoteReference
                                  )}%20-%20Missing%20Services"
                                  class="btn btn-outline"
                              >
                                  Contact Support
                              </a>
                          </div>
                      `}
            </div>
        `;
    }

    _handleQuoteAccepted() {
        // Forward the event to parent with quoteData
        this.dispatchEvent(
            new CustomEvent('quote-accept', {
                detail: { quoteData: this.quoteData },
                bubbles: true,
                composed: true,
            })
        );
    }

    _handleReloadQuote() {
        // Bubble event up to quote-details-page
        this.dispatchEvent(
            new CustomEvent('reload-quote', {
                bubbles: true,
                composed: true,
            })
        );
    }
}

customElements.define('quote-pending-acceptance', QuotePendingAcceptance);
